import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetFeedback } from '@/shared/hooks/useInteractions';
import { useEmployee } from '@/shared/hooks';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { ConversationDetails } from '@/routes/_dashboard/interactions/OpenBlockage/-ConversationDetails';
import { ActionPlan } from '@/routes/_dashboard/interactions/OpenBlockage/-ActionPlan';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/Tabs';
import { FeedbackStatus } from '@momentum/types';
import { useUpdateFeedback } from '@/shared/hooks/useInteractions';
import { UserSelectorAvatar } from '@/shared/components/user-selector';

interface FeedbackBlockageContentProps {
  feedbackId: string;
}

export const FeedbackBlockageContent = ({
  feedbackId,
}: FeedbackBlockageContentProps) => {
  const { t } = useTranslation('openBlockage');
  const {
    isSuccess,
    error: updateFeedbackError,
    mutate: updateFeedbackMutation,
  } = useUpdateFeedback();
  const { data: feedback, isLoading: isFeedbackLoading } =
    useGetFeedback(feedbackId);
  const user = useEmployeeContext();
  const { data: feedbackCreator, isLoading: isFeedbackCreatorLoading } =
    useEmployee(feedback?.creatorId, feedback?.companyId);
  const { data: feedbackReceiver } = useEmployee(
    feedback?.receiverId,
    feedback?.companyId
  );
  const [activeTab, setActiveTab] = useState<
    'conversation-details' | 'action-plan'
  >('conversation-details');

  useEffect(() => {
    if (feedback?.status) {
      setActiveTab(
        feedback.status !== FeedbackStatus.SENT
          ? 'action-plan'
          : 'conversation-details'
      );
    }
  }, [feedback?.status]);

  useEffect(() => {
    if (isSuccess) {
      setActiveTab('action-plan');
    }
  }, [isSuccess]);

  if (isFeedbackLoading || isFeedbackCreatorLoading)
    return <div>{t('loading')}</div>;
  if (!feedback || !feedback.formData)
    return <div>{t('noFeedbackFormData')}</div>;
  if (!feedbackCreator) return <div>{t('noFeedbackCreator')}</div>;
  if (!feedbackReceiver) return <div>{t('noFeedbackReceiver')}</div>;

  if (
    feedback.creatorId === user._id &&
    feedback.status === FeedbackStatus.SENT
  )
    return (
      <section className="flex gap-2 items-center justify-center h-3/5">
        {t('waitingFor')}
        <span className="flex items-center gap-2 font-semibold">
          <UserSelectorAvatar
            fullName={
              feedbackReceiver?.personalInfo.firstName +
              ' ' +
              feedbackReceiver?.personalInfo.lastName
            }
            imageUrl={feedbackReceiver?.personalInfo.imageUrl}
          />
          {feedbackReceiver?.personalInfo.firstName +
            ' ' +
            feedbackReceiver?.personalInfo.lastName}
        </span>
        {t('toSeeTheFeedbackYouSent')}
      </section>
    );

  if (feedback.receiverId !== user._id && feedback.creatorId !== user._id)
    return <section>{t('notAllowedToViewFeedback')}</section>;

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) =>
        setActiveTab(value as 'conversation-details' | 'action-plan')
      }
      className="flex flex-col items-center h-full mt-2"
    >
      <TabsList className="mx-auto">
        <TabsTrigger value="conversation-details">
          {t('conversationDetails')}
        </TabsTrigger>
        <TabsTrigger
          value="action-plan"
          disabled={feedback.status === FeedbackStatus.SENT}
        >
          {t('actionPlan')}
        </TabsTrigger>
      </TabsList>
      <TabsContent
        value="conversation-details"
        className="mt-10 flex flex-col items-center justify-center gap-2"
      >
        <ConversationDetails
          feedback={feedback}
          feedbackCreator={feedbackCreator}
          isAlreadyFinished={feedback.status !== FeedbackStatus.SENT}
          onAllYesClick={() => {
            updateFeedbackMutation({
              id: feedback._id,
              data: { status: FeedbackStatus.ACTION_PLAN },
            });
          }}
        />
        {updateFeedbackError && (
          <div className="text-destructive">
            {t('errorUpdatingFeedback')} {updateFeedbackError.message}
          </div>
        )}
      </TabsContent>
      <TabsContent value="action-plan" className="w-4/5 mt-10">
        <ActionPlan
          feedback={feedback}
          feedbackCreator={feedbackCreator}
          feedbackReceiver={feedbackReceiver}
        />
      </TabsContent>
    </Tabs>
  );
};
